import { useEffect, useId, useState, type FormEvent } from 'react';
import {
  BUTTON_CLASS,
  Checkbox,
  DetailDrawer,
  fieldStyle,
  neutralButtonStyle,
  primaryButtonStyle,
  StatusDot,
  token,
} from '@bp-files/ui';
import { messageFrom } from '@bp-files/client';
import { SPACE_LIST_COPY } from '@/components/SpaceList/spaceList.copy';
import {
  getAgencyConnections,
  inviteSpaceMember,
  VISIBILITY_SCOPES,
  type AgencyConnection,
  type VisibilityScope,
} from '@/lib/api';

/**
 * Invite one agency into a room that already exists — CON-6 through CON-9.
 *
 * **The other half of `ConnectPanel`, and the half that happens many times.**
 * The body is `POST /connect`'s, unchanged and moved nearly verbatim (ADR-0071):
 * an address and an attestation. What changed is that it is addressed to a
 * space, so the panel no longer has to invent a name for a room the API is about
 * to resolve a domain into — which is what made a brand-new agency produce a
 * room titled after its domain. `CreateSpacePanel`'s header carries that story.
 *
 * Which of the two branches runs is the API's decision, made on the address's
 * domain (SPEC §4.2); this panel only reports which one did, because they end in
 * different places — branch A waits on an admin who already exists, branch B
 * waits on a stranger to sign in for the first time.
 *
 * **Owner-only, and that is visibility rather than authorization.** The control
 * that opens this panel is withheld from a member that does not own the room
 * (CON-22, §5.3); the API refuses the request regardless of what was rendered
 * (SEC-5). ADR-0071 names the two refusals this act gained that the old one
 * never had — *you are not the owner* and *this agency is already a member* —
 * and both are 403s about a room rather than 400s about an address, so both are
 * shown in the API's own wording rather than translated into this form's voice.
 *
 * **Scope is chosen here, and it is the second of the two facts a member is
 * added with** (CON-23, CON-24). The API has accepted `visibilityScope` on this
 * route since the enforcement slice and nothing set it, so every membership this
 * form made was `all` by omission; now the choice is made where the arrangement
 * is made. Assembling the joint docket is four uses of this form at two scopes —
 * the prosecutor and the clerk at `all`, each police agency at `own` — which is
 * the shape CON-24 exists to serve and the reason the control belongs on the
 * invitation rather than only on the roster afterwards.
 *
 * **It differs from the attestation beside it in exactly one way, and the
 * difference decides the default.** `cjiCleared` records a judgment a person
 * made, so it is required by the API and unticked here — a default would put
 * words in somebody's mouth. Scope has a correct answer for an owner who does
 * not think about it, which is `all`: the room's whole contents, and what every
 * space did before CON-24 existed.
 *
 * Built on `DetailDrawer`, exactly as `CreateSpacePanel` is.
 */

export interface AddAgencyPanelProps {
  open: boolean;
  onClose: () => void;
  /** The room being added to. Its name titles the drawer; its id addresses the request. */
  spaceId: string;
  spaceName: string;
  /**
   * Called once the membership exists.
   *
   * **It carried a branch and no longer does** (ADR-0095). There were two
   * outcomes to tell apart — an agency that existed, and one minted out of the
   * address — and there is one act now, so the caller has one sentence to show.
   */
  onAdded: () => void;
}

export function AddAgencyPanel({
  open,
  onClose,
  spaceId,
  spaceName,
  onAdded,
}: AddAgencyPanelProps) {
  const agencyId = useId();
  const agencyHintId = useId();
  const cjiId = useId();
  const cjiHintId = useId();
  const scopeId = useId();
  const scopeHintId = useId();

  const [choice, setChoice] = useState('');
  const [connections, setConnections] = useState<AgencyConnection[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [cjiCleared, setCjiCleared] = useState(false);
  /** CON-24's default: the room's whole contents, which is what a room was. */
  const [visibilityScope, setVisibilityScope] = useState<VisibilityScope>('all');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();

  /*
    The picker's source (CON-28). A promise chain rather than an `async` effect
    body, on `usePlatformRead`'s rule: setting state on an effect's first
    synchronous tick cascades a render.

    **It reloads when the drawer opens**, not once per mount — the operator can
    change this list at any time, and the panel is not unmounted between uses.
  */
  useEffect(() => {
    if (!open) return;
    let live = true;

    getAgencyConnections()
      .then((result) => {
        if (!live) return;
        setConnections(result.connections);
        setLoadFailed(false);
      })
      .catch(() => {
        if (live) setLoadFailed(true);
      });

    return () => {
      live = false;
    };
  }, [open]);

  /** `void`-returning, with the async work nested — `CreateSpacePanel`'s rule. */
  function submit(event: FormEvent): void {
    event.preventDefault();
    if (pending) return;

    if (choice === '') {
      setError(SPACE_LIST_COPY.addAgency.errors.agencyRequired);
      return;
    }

    void send(choice);
  }

  async function send(chosen: string): Promise<void> {
    setPending(true);
    setError(undefined);

    try {
      // `visibilityScope` is sent even at its default. The API reads an absent
      // value as `all`, so omitting it would be a valid request — and a form
      // whose state says one thing while its body says nothing is a form that
      // can silently stop agreeing with itself.
      await inviteSpaceMember(spaceId, {
        agencyId: chosen,
        cjiCleared,
        visibilityScope,
      });

      setChoice('');
      setCjiCleared(false);
      // Back to the default, on the same reasoning as the two above: an admin
      // adding six agencies in a row must not carry one invitation's
      // compartmentation into the next. The reset outlives the reason it was
      // written for — the panel handed over on success now rather than staying
      // open — because this form is not unmounted between uses.
      setVisibilityScope('all');
      onAdded();
    } catch (caught) {
      // CON-8 as amended: the refusal names the agency and says to contact Blue
      // Partner — the one actionable thing in the response, so it is not
      // thrown away.
      setError(messageFrom(caught, SPACE_LIST_COPY.addAgency.errors.failed));
    } finally {
      setPending(false);
    }
  }

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={SPACE_LIST_COPY.addAgency.title(spaceName)}
      closeLabel={SPACE_LIST_COPY.addAgency.close}
    >
      <form onSubmit={submit} className="flex flex-col gap-5">
        <p className="text-sm" style={{ color: token('bp-text-muted') }}>
          {SPACE_LIST_COPY.addAgency.intro}
        </p>

        {/*
          **A picker, not a text field** (CON-5, ADR-0095). It was an address,
          because branch B minted an agency out of one; agencies come from the
          operator now, so the admin chooses from the list Blue Partner curates
          for their agency (CON-27, CON-28).

          A native `select` rather than `@bp-files/ui`'s `SearchableSelect`: a
          connection list is a handful of counterparts, where the console's
          picker is drawn from the whole fleet.
        */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor={agencyId} className="text-xs font-medium">
            {SPACE_LIST_COPY.addAgency.fields.agency}
          </label>
          <select
            id={agencyId}
            name="agencyId"
            value={choice}
            aria-describedby={agencyHintId}
            onChange={(event) => {
              setChoice(event.target.value);
            }}
            className="border px-3 py-2 text-sm"
            style={fieldStyle()}
          >
            <option value="">{SPACE_LIST_COPY.addAgency.fields.agencyPlaceholder}</option>
            {connections.map((agency) => (
              <option key={agency.agencyId} value={agency.agencyId}>
                {SPACE_LIST_COPY.addAgency.fields.agencyOption(agency.name, agency.domain)}
              </option>
            ))}
          </select>
          <span id={agencyHintId} className="text-xs" style={{ color: token('bp-text-muted') }}>
            {loadFailed
              ? SPACE_LIST_COPY.addAgency.errors.connectionsFailed
              : connections.length === 0
                ? SPACE_LIST_COPY.addAgency.fields.agencyNone
                : SPACE_LIST_COPY.addAgency.fields.agencyHint}
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-start gap-2">
            <Checkbox
              id={cjiId}
              checked={cjiCleared}
              describedBy={cjiHintId}
              onChange={setCjiCleared}
              label={SPACE_LIST_COPY.addAgency.fields.cjiCleared}
            />
            <label htmlFor={cjiId} className="text-xs font-medium">
              {SPACE_LIST_COPY.addAgency.fields.cjiCleared}
            </label>
          </div>
          <span id={cjiHintId} className="text-xs" style={{ color: token('bp-text-muted') }}>
            {SPACE_LIST_COPY.addAgency.fields.cjiClearedHint}
          </span>
        </div>

        {/*
          CON-24, as a radio group rather than a select: two values (ADR-0067),
          both of which the admin should be able to read without opening
          anything. A `fieldset`/`legend` because the question is about the group
          and each option needs its own label — the pattern `MembersTab`'s access
          level uses for the same reason.
        */}
        <fieldset className="flex flex-col gap-1.5 border-0 p-0">
          <legend className="text-xs font-medium">
            {SPACE_LIST_COPY.addAgency.fields.visibilityScope}
          </legend>
          <div className="mt-2 flex flex-col gap-1.5" aria-describedby={scopeHintId}>
            {VISIBILITY_SCOPES.map((scope) => (
              <div key={scope} className="flex items-center gap-2">
                <input
                  id={`${scopeId}-${scope}`}
                  name={`${scopeId}-visibilityScope`}
                  type="radio"
                  value={scope}
                  checked={visibilityScope === scope}
                  onChange={() => {
                    setVisibilityScope(scope);
                  }}
                />
                <label htmlFor={`${scopeId}-${scope}`} className="text-xs">
                  {SPACE_LIST_COPY.addAgency.scopes[scope]}
                </label>
              </div>
            ))}
          </div>
          <span id={scopeHintId} className="text-xs" style={{ color: token('bp-text-muted') }}>
            {SPACE_LIST_COPY.addAgency.fields.visibilityScopeHint}
          </span>
        </fieldset>

        {error === undefined ? null : (
          <p role="alert" className="flex items-start gap-2 text-sm text-danger">
            <StatusDot tone="alert" />
            {error}
          </p>
        )}

        {/*
          **No success line here.** It reported which branch ran and the panel
          stayed open around it, which left an admin on a spent form holding an
          answer about a roster they could not see. `onAdded` carries the branch
          to `SpaceList`, which shows the same sentence on the members panel it
          hands over to — the failure path above stays, because a refusal is
          about the address still in this field.
        */}
        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={pending}
            className={BUTTON_CLASS}
            style={primaryButtonStyle()}
          >
            {pending ? SPACE_LIST_COPY.addAgency.submitting : SPACE_LIST_COPY.addAgency.submit}
          </button>

          <button
            type="button"
            onClick={onClose}
            className={BUTTON_CLASS}
            style={neutralButtonStyle()}
          >
            {SPACE_LIST_COPY.addAgency.cancel}
          </button>
        </div>
      </form>
    </DetailDrawer>
  );
}
